import { ColDef, ColGroupDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useRef } from "react";
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { TQC1040_R01Dto } from "./Dto_TQC1040_R01";
import useTQC1040_R01Store from "./Store_TQC1040_R01";
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CreateCellDoubleClickHandler,
	CustomCellClickAction,
	CustomCellDoubleClickAction,
	getSwitchButtonColumnOptions,
	GridCellConfig,
	useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useTQC1040_R01Modal } from "./hooks/useTQC1040_R01Modal";
import { useTQC1040_R01CellChange } from "./hooks/TQC1040_R01CellChange";
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = "TQC1040_R01-grid-column-state";

// 검사항목/검사자/비고만 편집 가능하고 나머지는 조회값이다.
const headerNames: Record<string, string> = {
	rowIndex: "",
	inspectDt: "일자",
	eqmCd: "약품코드",
	equipmentNm: "약품명",
	customNm: "공급처",
	transportQty: "수량",
	eqmUnit: "단위",
	inspectValue1: "누출없을것",
	inspectValue2: "약품명부착",
	inspectValue3: "납품일자기록",
	inspectValue4: "유독물경고",
	inspectValue5: "터짐없음",
	inspectValue6: "M.Sheet제출",
	inspectEmpno: "검사자",
	inspectEmpNm: "검사자명",
	remark: "비고",
};

// EQM2040_R01 패턴: 그룹 구조를 유지하며 헤더 클래스를 정규화한다.
// 그룹 헤더에는 group-header 클래스를, 리프 헤더에는 기본 헤더 클래스를 부여한다.
const normalizeHeaderClass = (
	defs: (ColDef<TQC1040_R01Dto> | ColGroupDef<TQC1040_R01Dto>)[],
): (ColDef<TQC1040_R01Dto> | ColGroupDef<TQC1040_R01Dto>)[] =>
	defs.map((def: any) => {
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;

		if (children) {
			const groupHeaderClass = [def.headerClass, "common-group-header"]
				.filter(Boolean).join(" ");
			return { ...def, headerClass: groupHeaderClass, suppressMenu: true, children } as ColGroupDef<TQC1040_R01Dto>;
		}
		const mergedHeaderClass = [aggridHeaderClass, def.headerClass]
			.filter(Boolean).join(" ");
		return { ...def, headerClass: mergedHeaderClass, suppressMenu: true } as ColDef<TQC1040_R01Dto>;
	});

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<TQC1040_R01Dto> | null>;
	defaultTQC1040_R01Row: TQC1040_R01Dto;
}

export const Table_TQC1040_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultTQC1040_R01Row } = props;

	const {
		TQC1040_R01List, setTQC1040_R01List,
		updateTQC1040_R01List,
		selectedTQC1040_R01List, addSelectedTQC1040_R01List, removeSelectedTQC1040_R01List,
		initSelectedTQC1040_R01List, allSelectedTQC1040_R01List,
		originalTQC1040_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useTQC1040_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: TQC1040_R01List,
		defaultGridData: defaultTQC1040_R01Row,
		selectedRow: selectedTQC1040_R01List,
		originalGridData: originalTQC1040_R01List,
		updateRow: (index: number, data: any) => updateTQC1040_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ["inspectEmpNm"],
		insertedDisabledCells: ["inspectEmpNm"],
		commonDisabledCells: ["inspectDt", "eqmCd", "equipmentNm", "customNm", "transportQty", "eqmUnit"],

		pointerCells: ["inspectEmpno", "remark"],
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: [],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<TQC1040_R01Dto>({
			idKey: "valueIndex",
			selectedList: selectedTQC1040_R01List,
			deletedList: [],
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		createCellClassRules, selectedTQC1040_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
	]);

	const editableColumns = ["remark"];

	const { settingCodeSearchModalProps } = useTQC1040_R01Modal({
		updateTQC1040_R01List,
		gridRef,
	});

	// 검사항목 Y/N 스위치 버튼 컬럼 공통 정의 (SYS2010_R01 패턴)
	const ynColumn = (colId: string, headerName: string): ColDef<TQC1040_R01Dto> => ({
		headerName, colId, field: colId as keyof TQC1040_R01Dto, width: 100,
		...getSwitchButtonColumnOptions(
			colId as keyof TQC1040_R01Dto,
			handleSwitchButtonHandler,
			headerNames,
			aggridSwitchButtonWrapperStyle,
			true,
		),
	});

	const baseColumnDefs: (ColDef<TQC1040_R01Dto> | ColGroupDef<TQC1040_R01Dto>)[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "일자", colId: "inspectDt", field: "inspectDt", width: 110,
			editable: false,
			valueFormatter: (params) =>
				params.data?.inspectDt ? formatDate(new Date(params.data.inspectDt)) : "",
			comparator: cellDateComparator,
		},
		{
			headerName: "약품코드", colId: "eqmCd", field: "eqmCd", width: 120,
			editable: false,
			cellStyle: { textAlign: "left" },
		},
		{
			headerName: "약품명", colId: "equipmentNm", field: "equipmentNm", width: 180,
			editable: false,
			cellStyle: { textAlign: "left" },
		},
		{
			headerName: "공급처", colId: "customNm", field: "customNm", width: 140,
			editable: false,
		},
		{
			headerName: "수량", colId: "transportQty", field: "transportQty", width: 90,
			editable: false,
			valueFormatter: (params) => (params.value ? Number(params.value).toLocaleString() : "0"),
			cellStyle: { textAlign: "right" },
		},
		{
			headerName: "단위", colId: "eqmUnit", field: "eqmUnit", width: 70,
			editable: false, cellStyle: { textAlign: "center" },
		},
		{
			headerName: "검사항목",
			headerClass: "common-group-header",
			children: [
				ynColumn("inspectValue1", "누출없을것"),
				ynColumn("inspectValue2", "약품명부착"),
				ynColumn("inspectValue3", "납품일자기록"),
				ynColumn("inspectValue4", "유독물경고"),
				ynColumn("inspectValue5", "터짐없음"),
				ynColumn("inspectValue6", "M.Sheet제출"),
			],
		},
		{
			headerName: "검사자", colId: "inspectEmpno", field: "inspectEmpno", width: 90,
			editable: true, pasteable: true,
		},
		{
			headerName: "검사자명", colId: "inspectEmpNm", field: "inspectEmpNm", width: 100,
			editable: false,
		},
		{
			headerName: "비고", colId: "remark", field: "remark", width: 200,
			editable: true, pasteable: true,
		},
	// eslint-disable-next-line react-hooks/exhaustive-deps
	], [
		cellDateComparator, handleSwitchButtonHandler, createCellClassRules,
	]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: (ColDef<TQC1040_R01Dto> | ColGroupDef<TQC1040_R01Dto>)[] = useMemo(() => {
		const merged = GridCustomColumnDefs<TQC1040_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
		// EQM2040_R01 패턴: 그룹 헤더에는 group-header 클래스, 리프 헤더에는 기본 헤더 클래스를 부여한다.
		return normalizeHeaderClass(merged);
	}, [baseColumnDefs, columnState]);

	const { handleCellChange } = useTQC1040_R01CellChange({
		updateTQC1040_R01List,
		originalTQC1040_R01List,
	});

	const handleRowClick = useRowSelectionHandler({
		dataList: TQC1040_R01List,
		idKey: "valueIndex",
		selectedList: selectedTQC1040_R01List,
		deletedList: [],
		addSelected: addSelectedTQC1040_R01List,
		removeSelected: removeSelectedTQC1040_R01List,
		clickable, setClickable,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: TQC1040_R01List as any,
		editableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	// 검사항목/비고 셀 클릭 시 편집 시작
	const cellClickActions: CustomCellClickAction<TQC1040_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => editableColumns.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	// eslint-disable-next-line react-hooks/exhaustive-deps
	], []);
	const handleCellClick = CreateCellClickHandler(TQC1040_R01List, "valueIndex", cellClickActions);

	// 검사자 셀 더블클릭 시 사원 검색 팝업
	const cellDoubleClickActions: CustomCellDoubleClickAction<TQC1040_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => field === "inspectEmpno",
			action: (_e, foundData) => {
				settingCodeSearchModalProps(
					"검사자",
					foundData.inspectEmpno || "",
					false,
					"inspectEmpno",
					foundData,
					foundData.valueIndex,
				);
			},
		},
	], [settingCodeSearchModalProps]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(TQC1040_R01List, "valueIndex", cellDoubleClickActions);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: "",
		inspectDt: `${TQC1040_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [TQC1040_R01List.length]);

	return (
		<GridContextWrapper<TQC1040_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setTQC1040_R01List}
			initSelected={initSelectedTQC1040_R01List}
			allSelected={allSelectedTQC1040_R01List}
			defaultRowData={defaultTQC1040_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
		>
			<Grid_Excel<TQC1040_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<TQC1040_R01Dto>({
					idKey: "valueIndex",
					selectedList: selectedTQC1040_R01List,
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
